import { Alert } from '@design-system';
import { useTranslation } from 'next-i18next';

import { purchasingState } from '../../../helpers/purchasing';
import { Frame } from '../../../types/Products';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { FramePicker } from '../../Containers/ReviewCheckoutDrawer/FramePicker';
import Drawer from '../../Layout/Drawer';

interface Props<T extends ProductPurchaseDetails> {
  product: ProductType;
  shown: boolean;
  purchasingController: ProductPurchasingController<T>;
  onClose: () => void;
  onSubmit?: (frame: Frame | null) => void;
}

export const ProductFramingDrawer = <T extends ProductPurchaseDetails>({
  onClose,
  onSubmit,
  product,
  purchasingController,
  shown,
}: Props<T>) => {
  const { t } = useTranslation(['product']);

  if (
    !purchasingController ||
    purchasingController.product === null ||
    purchasingController.frameOptions?.length === 0
  ) {
    return null;
  }

  if (!shown) {
    return <Drawer onClose={onClose} shown={shown} />;
  }

  const productSubtotal = purchasingController.collectionSet
    ? purchasingController.collectionSet
        .filter(({ selected }) => selected)
        .reduce((acc, item) => acc + item.price * item.quantity, 0)
    : (purchasingController.product?.price ?? 0) *
      purchasingController.quantity;

  const exploratory =
    !onSubmit &&
    purchasingState({
      releaseType: product.releaseType,
      closingDate: product.closingDate,
      unlocked: purchasingController.unlocked,
      available: purchasingController.product?.available,
      tags: purchasingController.product?.tags,
    }) !== 'AVAILABLE';

  return (
    <Drawer onClose={onClose} shown={shown}>
      <div className="mx-auto w-full max-w-md">
        {purchasingController.frameOptions?.length ? (
          <FramePicker
            description={
              product?.framing?.[0]?.description ||
              product.details[0].framingOptions
            }
            frameByDefault={purchasingController.isFramedByDefault}
            frameOptions={purchasingController.frameOptions}
            frameSelected={purchasingController.frame}
            hideSubmitButton={exploratory}
            hideTitle={false}
            includeUnframed
            onSubmit={(frame) => {
              purchasingController.setFrame(frame);
              onSubmit?.(frame);
              onClose();
            }}
            productSubtotal={productSubtotal}
            submitButtonLabel={t('drawers.review.framing.cta')}
          />
        ) : (
          <div className="flex flex-col gap-9">
            <div className="mb-6 hidden w-full flex-1 md:flex">
              <p className="text-md font-bold md:text-lg">
                {t('drawers.review.framing.title')}
              </p>
            </div>
            <Alert status="error">
              No frames found for the selected works.
            </Alert>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ProductFramingDrawer;
